import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import customerService from '../services/CustomerService';
import appointmentService from '../services/AppointmentService';
import treatmentRecordService from '../services/TreatmentRecordService';
import practitionerService from '../services/PractitionerService';

/**
 * Home Page component serving as the dashboard
 * @returns {React.ReactElement} HomePage component
 */
const HomePage = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalAppointments: 0,
    totalTreatmentRecords: 0,
    totalPractitioners: 0,
    todayAppointments: 0,
    weekAppointments: 0
  });
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Load summary statistics
        const customers = customerService.getAllCustomers();
        const appointments = appointmentService.getAllAppointments();
        const treatmentRecords = treatmentRecordService.getAllTreatmentRecords();
        const practitioners = practitionerService.getAllPractitioners();

        // Calculate today's and this week's appointments
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const endOfToday = new Date(today);
        endOfToday.setHours(23, 59, 59, 999);
        
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
        
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6); // End of week (Saturday)
        endOfWeek.setHours(23, 59, 59, 999);
        
        const todayAppointments = appointments.filter(appointment => {
          const appointmentDate = new Date(appointment.startTime);
          return appointmentDate >= today && appointmentDate <= endOfToday;
        });
        
        const weekAppointments = appointments.filter(appointment => {
          const appointmentDate = new Date(appointment.startTime);
          return appointmentDate >= today && appointmentDate <= endOfWeek;
        });

        // Get recent appointments (last 5)
        const sortedAppointments = [...appointments].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        const recent = sortedAppointments.slice(0, 5);
        
        // Enrich appointment data with customer and practitioner names
        const enrichedRecent = recent.map(appointment => {
          const customer = customers.find(c => c.id === appointment.customerId);
          const practitioner = practitioners.find(p => p.id === appointment.practitionerId);
          return {
            ...appointment,
            customerName: customer ? customer.name : '未知客户',
            practitionerName: practitioner ? practitioner.name : '未知医师'
          };
        });

        // Get upcoming appointments (next 5)
        const now = new Date();
        const upcoming = [...appointments]
          .filter(appointment => new Date(appointment.startTime) > now)
          .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
          .slice(0, 5);
        
        // Enrich upcoming appointment data
        const enrichedUpcoming = upcoming.map(appointment => {
          const customer = customers.find(c => c.id === appointment.customerId);
          const practitioner = practitioners.find(p => p.id === appointment.practitionerId);
          return {
            ...appointment,
            customerName: customer ? customer.name : '未知客户',
            practitionerName: practitioner ? practitioner.name : '未知医师'
          };
        });

        // Update state with fetched data
        setStats({
          totalCustomers: customers.length,
          totalAppointments: appointments.length,
          totalTreatmentRecords: treatmentRecords.length,
          totalPractitioners: practitioners.length,
          todayAppointments: todayAppointments.length,
          weekAppointments: weekAppointments.length
        });
        setRecentAppointments(enrichedRecent);
        setUpcomingAppointments(enrichedUpcoming);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '未设置';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return '无效日期';
    }
    
    return new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-100 p-6 rounded-lg shadow">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 rounded mb-2"></div>
              ))}
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 rounded mb-2"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Welcome message */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          欢迎回来，{currentUser?.name || '用户'}
        </h1>
        <p className="text-gray-600">今天是 {new Intl.DateTimeFormat('zh-CN', { dateStyle: 'full' }).format(new Date())}</p>
      </div>
      
      {/* Statistics cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
          <p className="text-sm font-medium text-gray-500 mb-1">总客户数</p>
          <p className="text-2xl font-bold text-gray-900">{stats.totalCustomers}</p>
          <Link to="/customers" className="text-sm text-blue-500 hover:underline mt-2 inline-block">
            查看所有客户
          </Link>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
          <p className="text-sm font-medium text-gray-500 mb-1">总预约数</p>
          <p className="text-2xl font-bold text-gray-900">{stats.totalAppointments}</p>
          <div className="flex text-sm mt-2">
            <span className="text-gray-500">今日：</span>
            <span className="font-medium ml-1">{stats.todayAppointments}</span>
            <span className="text-gray-500 ml-2">本周：</span>
            <span className="font-medium ml-1">{stats.weekAppointments}</span>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-purple-500">
          <p className="text-sm font-medium text-gray-500 mb-1">治疗记录</p>
          <p className="text-2xl font-bold text-gray-900">{stats.totalTreatmentRecords}</p>
          <Link to="/treatment-records" className="text-sm text-blue-500 hover:underline mt-2 inline-block">
            查看所有记录
          </Link>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-yellow-500">
          <p className="text-sm font-medium text-gray-500 mb-1">调理师数量</p>
          <p className="text-2xl font-bold text-gray-900">{stats.totalPractitioners}</p>
          <Link to="/practitioners" className="text-sm text-blue-500 hover:underline mt-2 inline-block">
            查看所有调理师
          </Link>
        </div>
      </div>
      
      {/* Content area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent appointments */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4 text-gray-900">近期预约</h2>
          
          {recentAppointments.length > 0 ? (
            <div className="space-y-3">
              {recentAppointments.map(appointment => (
                <Link 
                  key={appointment.id}
                  to={`/appointments/${appointment.id}`}
                  className="block p-4 border border-gray-100 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{appointment.customerName}</p>
                      <p className="text-sm text-gray-600">医师: {appointment.practitionerName}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        预约时间: {formatDate(appointment.startTime)}
                      </p>
                    </div>
                    <span className={`text-sm px-2 py-1 rounded font-medium ${
                      appointment.status === 'completed' ? 'bg-green-100 text-green-800' : 
                      appointment.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {appointment.status === 'scheduled' ? '已预约' : 
                       appointment.status === 'completed' ? '已完成' : 
                       appointment.status === 'cancelled' ? '已取消' : 
                       appointment.status === 'checked_in' ? '已签到' : appointment.status}
                    </span>
                  </div>
                </Link>
              ))}
              
              <div className="pt-3 text-right">
                <Link to="/appointments" className="text-sm text-blue-500 hover:underline">
                  查看所有预约 →
                </Link>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 py-4">暂无近期预约记录</p>
          )}
        </div>
        
        {/* Upcoming appointments */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4 text-gray-900">今日待办</h2>
          
          {upcomingAppointments.length > 0 ? (
            <div className="space-y-3">
              {upcomingAppointments.map(appointment => (
                <Link 
                  key={appointment.id}
                  to={`/appointments/${appointment.id}`}
                  className="block p-4 border border-gray-100 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex justify-between">
                    <div>
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="font-medium text-gray-900">{appointment.customerName}</p>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">医师: {appointment.practitionerName}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        预约时间: {formatDate(appointment.startTime)}
                      </p>
                    </div>
                    
                    <div className="flex flex-col items-end">
                      <span className="text-sm px-2 py-1 rounded font-medium bg-yellow-100 text-yellow-800">
                        即将到来
                      </span>
                      
                      {/* Calculate time remaining */}
                      <span className="text-xs text-gray-500 mt-2">
                        {(() => {
                          const now = new Date();
                          const appointmentTime = new Date(appointment.startTime);
                          const diffMs = appointmentTime - now;
                          const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
                          const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                          
                          if (diffHrs > 0) {
                            return `${diffHrs}小时${diffMins}分钟后`;
                          } else if (diffMins > 0) {
                            return `${diffMins}分钟后`;
                          } else {
                            return '即将开始';
                          }
                        })()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
              
              <div className="pt-3 text-right">
                <Link to="/appointments" className="text-sm text-blue-500 hover:underline">
                  查看所有预约 →
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-10 bg-gray-50 rounded-lg">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">没有待办事项</h3>
              <p className="mt-1 text-sm text-gray-500">今天没有预约安排</p>
              <div className="mt-6">
                <Link
                  to="/appointments/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  新建预约
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;